import { supabase } from '@/lib/supabaseClient';

export interface PasswordResetRequest {
  email: string;
}

export interface PasswordResetConfirm {
  token: string;
  password: string;
}

// The backend answers 200 with `email_sent: false` when it created the link but the send failed.
async function parseAccessEmailResponse(response: Response): Promise<{ emailSent: boolean }> {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(body.detail || body.error || 'Failed to send the email');
  }
  return { emailSent: body.email_sent !== false };
}

// Use backend API for password reset (replaces edge function)
const BACKEND_URL = import.meta.env.VITE_BACKEND_URL || import.meta.env.VITE_PYTHON_BACKEND_URL || 'https://admin-api.joulescrm.com';

export const passwordResetService = {
  async requestPasswordReset(email: string): Promise<void> {
    try {
      const endpoint = `${BACKEND_URL}/api/v1/password-reset`;
      const requestBody = { action: 'request-reset', email };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.error || 'Failed to request password reset');
      }
    } catch (error) {
      console.error('Error in requestPasswordReset:', error);
      throw error;
    }
  },

  /**
   * Admin-initiated: sends the branded "Welcome — set your password" email. The welcome route
   * needs the admin's own session and the account the person belongs to.
   */
  async sendWelcomeEmail(email: string, tenantId: string): Promise<{ emailSent: boolean }> {
    const { data } = await supabase.auth.getSession();
    const token = data.session?.access_token;
    if (!token) throw new Error('Your session has expired. Sign in again and retry.');
    return parseAccessEmailResponse(
      await fetch(`${BACKEND_URL}/api/v1/password-reset/welcome`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email, tenant_id: tenantId }),
      }),
    );
  },

  /** Admin-initiated: the same email "Forgot password" sends, with whether Postmark took it. */
  async sendResetEmail(email: string): Promise<{ emailSent: boolean }> {
    return parseAccessEmailResponse(
      await fetch(`${BACKEND_URL}/api/v1/password-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'request-reset', email }),
      }),
    );
  },

  async verifyResetToken(token: string): Promise<{ email: string }> {
    try {
      const endpoint = `${BACKEND_URL}/api/v1/password-reset`;
      const requestBody = { action: 'verify-token', token };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.error || 'Invalid or expired reset token');
      }

      const data = await response.json();
      return { email: data.email };
    } catch (error) {
      console.error('Error verifying reset token:', error);
      throw error;
    }
  },

  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      // First verify the token
      await this.verifyResetToken(token);

      const endpoint = `${BACKEND_URL}/api/v1/password-reset`;
      const requestBody = { action: 'reset-password', token, newPassword };

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || errorData.error || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error in resetPassword:', error);
      throw error;
    }
  }
};